import * as THREE from "three";
import type { SampleRegion } from "@/core/field/brushes";
import type { VoxelField } from "@/core/field/VoxelField";
import { computeFieldAO, expandAOToColors } from "./fieldAO";
import { claySmoothMesh, computeCreasedNormals } from "./meshUtils";
import { SURFACE_POLISH } from "@/config/designer";
import { extractSurfaceNets } from "./surfaceNets";

/**
 * Display meshing, in chunks.
 *
 * A full re-mesh of a 300 mm blank is over a million cells — far too slow to
 * run on every pointer move. Instead the grid is diced into chunks and only the
 * chunks a stroke actually touched are rebuilt, which keeps a stroke in the
 * low single-digit milliseconds regardless of block size.
 *
 * Each chunk is extracted with a one-cell halo so crease normals are computed
 * with the neighbouring geometry present; the halo triangles are then dropped,
 * leaving seamless shading and no duplicated faces. The export path does one
 * clean full-grid extraction instead — display speed and export integrity are
 * separate problems and are solved separately.
 */
export class ChunkedMesher {
  readonly group = new THREE.Group();

  private readonly cells: number;
  private readonly ncx: number;
  private readonly ncy: number;
  private readonly ncz: number;
  private readonly meshes = new Map<number, THREE.Mesh>();
  private readonly pending = new Set<number>();

  private lastRebuildMs = 0;
  private lastRebuiltChunks = 0;
  private triangles = 0;

  constructor(
    private readonly field: VoxelField,
    private material: THREE.Material,
    chunkCells = 24,
  ) {
    this.cells = chunkCells;
    this.ncx = Math.max(1, Math.ceil((field.nx - 1) / chunkCells));
    this.ncy = Math.max(1, Math.ceil((field.ny - 1) / chunkCells));
    this.ncz = Math.max(1, Math.ceil((field.nz - 1) / chunkCells));
    this.group.name = "hold";
    this.markAllDirty();
  }

  get triangleCount(): number {
    return this.triangles;
  }
  get lastRebuildDurationMs(): number {
    return this.lastRebuildMs;
  }
  get lastChunkCount(): number {
    return this.lastRebuiltChunks;
  }
  get pendingChunks(): number {
    return this.pending.size;
  }
  get chunkCount(): number {
    return this.ncx * this.ncy * this.ncz;
  }

  setMaterial(material: THREE.Material): void {
    this.material = material;
    for (const mesh of this.meshes.values()) mesh.material = material;
  }

  markAllDirty(): void {
    for (let id = 0; id < this.chunkCount; id++) this.pending.add(id);
  }

  markRegionDirty(region: SampleRegion): void {
    const c = this.cells;
    const x0 = Math.max(0, Math.floor((region.i0 - 2) / c));
    const x1 = Math.min(this.ncx - 1, Math.floor((region.i1 + 1) / c));
    const y0 = Math.max(0, Math.floor((region.j0 - 2) / c));
    const y1 = Math.min(this.ncy - 1, Math.floor((region.j1 + 1) / c));
    const z0 = Math.max(0, Math.floor((region.k0 - 2) / c));
    const z1 = Math.min(this.ncz - 1, Math.floor((region.k1 + 1) / c));
    for (let z = z0; z <= z1; z++) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          this.pending.add(x + this.ncx * (y + this.ncy * z));
        }
      }
    }
  }

  /**
   * Rebuild dirty chunks. With a time budget, work spills into later frames so
   * a big edit never drops a frame outright.
   */
  flush(budgetMs = Infinity): boolean {
    if (this.pending.size === 0) return false;
    const started = performance.now();
    let done = 0;
    for (const id of Array.from(this.pending)) {
      this.rebuildChunk(id);
      this.pending.delete(id);
      done++;
      if (performance.now() - started > budgetMs) break;
    }
    this.lastRebuildMs = performance.now() - started;
    this.lastRebuiltChunks = done;

    let total = 0;
    for (const mesh of this.meshes.values()) {
      const attr = mesh.geometry.getAttribute("position");
      total += attr ? attr.count / 3 : 0;
    }
    this.triangles = total;
    return true;
  }

  private rebuildChunk(id: number): void {
    const cx = id % this.ncx;
    const cy = Math.floor(id / this.ncx) % this.ncy;
    const cz = Math.floor(id / (this.ncx * this.ncy));
    const c = this.cells;

    const coreMin: [number, number, number] = [cx * c, cy * c, cz * c];
    const coreMax: [number, number, number] = [
      Math.min((cx + 1) * c, this.field.nx - 1),
      Math.min((cy + 1) * c, this.field.ny - 1),
      Math.min((cz + 1) * c, this.field.nz - 1),
    ];
    const grow = (v: readonly [number, number, number], by: number): [number, number, number] => [
      v[0] + by,
      v[1] + by,
      v[2] + by,
    ];

    const polishReach = SURFACE_POLISH.viewportIterations * 2;
    const extracted = extractSurfaceNets(this.field, {
      // Two adjacency rings per Taubin pair, plus one sampling guard ring,
      // keep independently rebuilt chunks identical at their shared edge.
      cellMin: grow(coreMin, -(polishReach + 1)),
      cellMax: grow(coreMax, polishReach + 1),
      emitMin: grow(coreMin, -polishReach),
      emitMax: grow(coreMax, polishReach),
      coreMin,
      coreMax,
    });

    const coreTriangles = extracted.coreTriangleCount ?? extracted.indices.length / 3;
    if (coreTriangles === 0) {
      this.disposeChunk(id);
      return;
    }

    const polished = claySmoothMesh(extracted, this.field, SURFACE_POLISH.viewportIterations);
    const shaded = computeCreasedNormals(polished);
    const positions = new Float32Array(shaded.positions.subarray(0, coreTriangles * 9));
    const normals = new Float32Array(shaded.normals.subarray(0, coreTriangles * 9));
    const colors = expandAOToColors(
      computeFieldAO(this.field, polished.positions, 0.18),
      polished.indices,
      coreTriangles,
    );

    let mesh = this.meshes.get(id);
    if (!mesh) {
      mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = true;
      // Picking goes through the voxel field, never the display mesh.
      mesh.raycast = () => undefined;
      this.meshes.set(id, mesh);
      this.group.add(mesh);
    }
    const geometry = mesh.geometry;
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
  }

  private disposeChunk(id: number): void {
    const mesh = this.meshes.get(id);
    if (!mesh) return;
    this.group.remove(mesh);
    mesh.geometry.dispose();
    this.meshes.delete(id);
  }

  dispose(): void {
    for (const id of Array.from(this.meshes.keys())) this.disposeChunk(id);
    this.pending.clear();
  }
}
